/**
 * ⟦A-15 §4⟧ **Les opportunités qui expirent** — collab, sponsor, invitation : une fenêtre qui
 * se referme.
 *
 * ── Pourquoi ce n'est pas un second système d'événements ────────────────────────
 *
 * A-15 §4 l'interdit explicitement : *« Complète le système d'événements existant, n'en crée
 * pas un second. »* Et c'est bien ce qui se passe — une opportunité **EST** un événement de
 * `content/events.json`, avec ses conditions, ses poids, ses choix, ses effets, résolu par la
 * **même action** `choix_evenement`. Un seul champ de contenu les sépare : `fenetre_h`.
 *
 * Ce qui change, c'est le **blocage**. Un `pending_event` bloque toute action (arbitrage
 * A24) : il ne peut donc pas expirer, puisque le joueur ne peut rien faire d'autre que d'y
 * répondre. Une opportunité, elle, reste sur la table pendant que le monde tourne — et le
 * monde peut la refermer. C'est exactement ce que la vie fait d'une proposition de collab :
 * elle n'interrompt pas le tournage en cours, elle attend. Un moment.
 *
 * ── Pourquoi un module séparé d'`events.ts` ────────────────────────────────────
 *
 * `events.ts` importe `HookContext` d'`actions.ts` ; `actions.ts` doit purger les fenêtres
 * échues à chaque avancée d'horloge. L'importer en retour fermerait un **cycle** — le même
 * raisonnement qui a fait de `coutMods` un hook ⟦A-11 §1⟧. Ce module ne dépend que de
 * `types.ts`, donc la flèche reste dans un seul sens. Précédent direct : `idees.ts`.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * `state.opportunites` est **optionnel** : `undefined` ⇔ `[]`. Et surtout, aucune opportunité
 * n'existe tant qu'aucun événement de contenu ne porte `fenetre_h` — la mécanique se coupe
 * donc **entièrement depuis `content/`**, sans toucher une ligne de moteur.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
import { H_JOUR } from "./clock.js";
/**
 * ⟦A-15 §4⟧ **Préavis d'une opportunité qui se referme** : un jour simulé ✎. Même repère que
 * les idées (`SEUIL_BIENTOT_PERIMEE_H`) et les vagues (`H_ALERTE_VAGUE`) — trois pressions,
 * un seul langage de préavis, donc rien de nouveau à apprendre pour le joueur.
 */
export const H_ALERTE_OPPORTUNITE = H_JOUR; // ✎
/** Les opportunités ouvertes — `undefined` ⇔ aucune (état neutre, jamais lu en direct). */
export function opportunites(state) {
    return state.opportunites ?? [];
}
/** L'opportunité ouverte portant cet id, si elle existe encore. */
export function opportuniteOuverte(state, eventId) {
    return opportunites(state).find((o) => o.event_id === eventId);
}
/**
 * Les offres sur la table, annotées. **C'est la moitié « prévenir » du garde-fou d'A-15** :
 * une fenêtre qui se referme doit se voir venir, sinon le joueur découvre une perte qu'il
 * n'avait aucun moyen d'anticiper — exactement le jeu anxiogène que l'avenant interdit.
 */
export function opportunitesLisibles(state) {
    return opportunites(state).map((o) => {
        const restantes = Math.max(0, o.expires_h - state.clock_h);
        return {
            event_id: o.event_id,
            drawn_at_h: o.drawn_at_h,
            expires_h: o.expires_h,
            heures_restantes: restantes,
            jours_restants: Math.floor(restantes / H_JOUR),
            bientot_fermee: restantes <= H_ALERTE_OPPORTUNITE,
        };
    });
}
/**
 * Referme les fenêtres échues. MUTE `state` ; rend les offres perdues.
 *
 * Appelée à **chaque avancée d'horloge** (pas seulement au sommeil) : depuis ⟦A-14⟧ une
 * evergreen coûte 64 h, et une collab proposée la veille doit pouvoir se refermer pendant
 * qu'on tourne. C'est précisément la tension qu'A-15 cherche — le monde continue sans toi.
 */
export function purgerOpportunites(state, events = [], log = []) {
    const encore = opportunites(state);
    if (encore.length === 0)
        return [];
    const perdues = encore.filter((o) => state.clock_h >= o.expires_h);
    if (perdues.length === 0)
        return [];
    state.opportunites = encore.filter((o) => state.clock_h < o.expires_h);
    for (const o of perdues) {
        events.push({
            kind: "warning",
            data: {
                scripted: "opportunite_expiree",
                event_id: o.event_id,
                ouverte_depuis_h: state.clock_h - o.drawn_at_h,
            },
        });
        log.push({
            turn: state.turn,
            clock_h: state.clock_h,
            famille: "evenement",
            text: "La fenêtre s'est refermée. Ils ont trouvé quelqu'un d'autre — ça arrive.",
        });
    }
    return perdues;
}
//# sourceMappingURL=opportunites.js.map